import React, { useState } from 'react';
import {
  Check, AlertCircle, CheckCircle, Loader2, Info,
  CheckSquare, Square, ChevronDown, ChevronUp, Users, GraduationCap
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Checkbox } from '../ui/checkbox';
import { Progress } from '../ui/progress';

const PILLAR_COLORS = {
  P1: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700', accent: 'text-blue-600' },
  P2: { bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', accent: 'text-emerald-600' },
  P3: { bg: 'bg-purple-50', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700', accent: 'text-purple-600' },
  P4: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700', accent: 'text-amber-600' },
  P5: { bg: 'bg-rose-50', border: 'border-rose-200', badge: 'bg-rose-100 text-rose-700', accent: 'text-rose-600' }
};

export const StepSelectMeasures = ({ formulas, selectedMeasures, onToggleMeasure, onSelectAll, onSelectPillar }) => {
  const groupedFormulas = formulas.reduce((acc, formula) => {
    if (!acc[formula.pillar]) {
      acc[formula.pillar] = { pillar: formula.pillar, pillar_name: formula.pillar_name, measures: [] };
    }
    acc[formula.pillar].measures.push(formula);
    return acc;
  }, {});

  const pillars = Object.values(groupedFormulas).sort((a, b) => a.pillar.localeCompare(b.pillar));
  const allSelected = formulas.length === selectedMeasures.length;

  return (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <h3 className="text-xl font-semibold text-gray-900">Select Measures to Import</h3>
        <p className="text-gray-500 mt-1">Choose which LEI measures you want to enter data for</p>
      </div>

      <div className="flex items-center justify-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={onSelectAll}
          className="text-sm"
          data-testid="select-all-measures"
        >
          {allSelected ? (
            <>
              <Square size={16} className="mr-2" />
              Deselect All
            </>
          ) : (
            <>
              <CheckSquare size={16} className="mr-2" />
              Select All ({formulas.length})
            </>
          )}
        </Button>
        <span className="text-sm text-gray-500">
          {selectedMeasures.length} of {formulas.length} selected
        </span>
      </div>

      <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
        {pillars.map(pillarGroup => {
          const pillarStyle = PILLAR_COLORS[pillarGroup.pillar] || PILLAR_COLORS.P1;
          const pillarMeasureIds = pillarGroup.measures.map(m => m.id);
          const selectedInPillar = pillarMeasureIds.filter(id => selectedMeasures.includes(id));
          const allPillarSelected = selectedInPillar.length === pillarGroup.measures.length;

          return (
            <div key={pillarGroup.pillar} className={`rounded-lg border ${pillarStyle.border} overflow-hidden`}>
              <div className={`p-3 ${pillarStyle.bg} flex items-center justify-between`}>
                <div className="flex items-center gap-2">
                  <Badge className={pillarStyle.badge}>{pillarGroup.pillar}</Badge>
                  <span className="font-medium text-gray-900">{pillarGroup.pillar_name}</span>
                  <span className="text-sm text-gray-500">
                    ({selectedInPillar.length}/{pillarGroup.measures.length})
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSelectPillar(pillarGroup.pillar, !allPillarSelected)}
                  className="text-xs h-7"
                >
                  {allPillarSelected ? 'Deselect Pillar' : 'Select Pillar'}
                </Button>
              </div>

              <div className="p-2 bg-white space-y-1">
                {pillarGroup.measures.map(measure => {
                  const isSelected = selectedMeasures.includes(measure.id);
                  return (
                    <label
                      key={measure.id}
                      className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                        isSelected ? 'bg-indigo-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => onToggleMeasure(measure.id)}
                        data-testid={`measure-checkbox-${measure.id}`}
                      />
                      <div className="flex-1">
                        <span className={`font-medium ${isSelected ? 'text-indigo-700' : 'text-gray-900'}`}>
                          {measure.name}
                        </span>
                        <span className="text-xs text-gray-400 ml-2">
                          {measure.component_count} components
                        </span>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const StepEnterValues = ({ formulas, selectedMeasures, measureValues, onValueChange }) => {
  const [expandedMeasure, setExpandedMeasure] = useState(selectedMeasures[0] || null);

  const selectedFormulas = formulas.filter(f => selectedMeasures.includes(f.id));

  const getCompletion = (measureId, formula) => {
    const values = measureValues[measureId] || {};
    const filledCount = Object.values(values).filter(v => v !== '' && v !== null && v !== undefined).length;
    return {
      filled: filledCount,
      total: formula.component_count,
      percent: formula.component_count > 0 ? (filledCount / formula.component_count) * 100 : 0
    };
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-xl font-semibold text-gray-900">Enter Component Values</h3>
        <p className="text-gray-500 mt-1">Fill in values for each measure (0-100 scale)</p>
      </div>

      <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
        <span className="text-sm text-gray-600">
          Measures with data: {selectedFormulas.filter(f => {
            const completion = getCompletion(f.id, f);
            return completion.filled > 0;
          }).length} / {selectedFormulas.length}
        </span>
        <span className="text-xs text-gray-500">
          Click a measure to expand/collapse
        </span>
      </div>

      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
        {selectedFormulas.map((formula) => {
          const pillarStyle = PILLAR_COLORS[formula.pillar] || PILLAR_COLORS.P1;
          const isExpanded = expandedMeasure === formula.id;
          const completion = getCompletion(formula.id, formula);
          const values = measureValues[formula.id] || {};

          return (
            <div key={formula.id} className={`rounded-lg border ${pillarStyle.border} overflow-hidden`}>
              <button
                onClick={() => setExpandedMeasure(isExpanded ? null : formula.id)}
                className={`w-full p-3 ${pillarStyle.bg} flex items-center justify-between text-left`}
                data-testid={`measure-accordion-${formula.id}`}
              >
                <div className="flex items-center gap-2">
                  <Badge className={pillarStyle.badge}>{formula.pillar}</Badge>
                  <span className="font-medium text-gray-900">{formula.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-24">
                    <Progress value={completion.percent} className="h-2" />
                  </div>
                  <span className="text-xs text-gray-500 w-12 text-right">
                    {completion.filled}/{completion.total}
                  </span>
                  {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </div>
              </button>

              {isExpanded && (
                <div className="p-3 bg-white space-y-3">
                  <p className="text-xs text-gray-500 font-mono mb-2">{formula.formula}</p>
                  {formula.components?.map((comp) => (
                    <div key={comp.id} className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <Label className="text-sm font-medium text-gray-800 truncate block">
                          {comp.name}
                        </Label>
                        <span className="text-xs text-gray-400">
                          {(comp.weight * 100).toFixed(0)}% weight
                          {comp.benchmark_low && ` • Benchmark: ${comp.benchmark_low}-${comp.benchmark_high}`}
                        </span>
                      </div>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        placeholder="0-100"
                        value={values[comp.id] || ''}
                        onChange={(e) => onValueChange(formula.id, comp.id, e.target.value)}
                        className="w-24 text-right font-mono"
                        data-testid={`bulk-input-${formula.id}-${comp.id}`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800 text-sm">
          You don't need to fill all components. Measures with at least one value will be imported.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export const StepReview = ({ formulas, selectedMeasures, measureValues, period, calculations, isCalculating, cohorts = [], programs = [], selectedCohortIds = [], selectedProgramIds = [] }) => {
  const selectedFormulas = formulas.filter(f => selectedMeasures.includes(f.id));
  
  const measuresWithData = selectedFormulas.filter(f => {
    const values = measureValues[f.id] || {};
    return Object.values(values).some(v => v !== '' && v !== null && v !== undefined);
  });

  const selectedCohortsList = cohorts.filter(c => selectedCohortIds.includes(c.id));
  const selectedProgramsList = programs.filter(p => selectedProgramIds.includes(p.id));

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-xl font-semibold text-gray-900">Review & Import</h3>
        <p className="text-gray-500 mt-1">Verify your data before importing</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-indigo-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-indigo-600">{measuresWithData.length}</p>
          <p className="text-xs text-indigo-700">Measures to Import</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-emerald-600">
            {Object.values(measureValues).reduce((sum, mv) => {
              return sum + Object.values(mv || {}).filter(v => v !== '' && v !== null).length;
            }, 0)}
          </p>
          <p className="text-xs text-emerald-700">Data Points</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-amber-600">{period}</p>
          <p className="text-xs text-amber-700">Period</p>
        </div>
      </div>

      {(selectedCohortsList.length > 0 || selectedProgramsList.length > 0) && (
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-sm font-medium text-gray-700 mb-2">Linked Segments</p>
          <div className="flex flex-wrap gap-2">
            {selectedCohortsList.map(c => (
              <Badge key={c.id} className="bg-blue-100 text-blue-700">
                <Users size={12} className="mr-1" />
                {c.name}
              </Badge>
            ))}
            {selectedProgramsList.map(p => (
              <Badge key={p.id} className="bg-purple-100 text-purple-700">
                <GraduationCap size={12} className="mr-1" />
                {p.name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {isCalculating ? (
        <div className="text-center py-8">
          <Loader2 className="animate-spin mx-auto text-indigo-600" size={32} />
          <p className="text-sm text-gray-500 mt-2">Calculating scores...</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[250px] overflow-y-auto pr-2">
          {measuresWithData.map(formula => {
            const pillarStyle = PILLAR_COLORS[formula.pillar] || PILLAR_COLORS.P1;
            const calc = calculations[formula.id];
            const values = measureValues[formula.id] || {};
            const filledCount = Object.values(values).filter(v => v !== '' && v !== null).length;

            return (
              <div key={formula.id} className={`p-3 rounded-lg border ${pillarStyle.border} ${pillarStyle.bg}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge className={pillarStyle.badge}>{formula.pillar}</Badge>
                    <span className="font-medium text-gray-900">{formula.name}</span>
                    <span className="text-xs text-gray-500">({filledCount} values)</span>
                  </div>
                  {calc ? (
                    <div className="text-right">
                      <span className="text-xl font-bold text-gray-900">
                        {calc.calculated_score?.toFixed(1)}
                      </span>
                      <span className="text-xs text-gray-500 ml-1">
                        ({calc.data_completeness?.toFixed(0)}%)
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">Pending...</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {measuresWithData.length === 0 && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800 text-sm">
            No measures have data entered. Go back and fill in at least one component value.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
